<script setup lang="ts">
const { t: $t } = useI18n()

import { diff } from 'deep-object-diff'
import type { RentalPricingMachineryTypeFilter } from '~/server/trpc/routers/shop/rentalPricing'

const props = defineProps<{
  isBasePriceSelection?: boolean
  showFilter?: boolean
}>()

const selectedMachineryTypes = defineModel<string[]>({ required: true })

// Search
const search = ref('')
const requireBasePriceExistence = computed(() => !props.isBasePriceSelection)

const filters = ref<RentalPricingMachineryTypeFilter>({})

// Data
const { rentalPricing } = useQuery()
const { data: machineryTypeOptionsData, isLoading: areMachineryTypesOptionsLoading } = rentalPricing.allMachineryTypeOptions(
  search,
  requireBasePriceExistence,
  filters,
)

type MachineryTypeOption = NonNullable<typeof machineryTypeOptionsData['value']>[number]

// Table
const machineryTypeOptionsColumns = computed<TableConfig<MachineryTypeOption>['columns']>(() => {
  const columns: TableConfig<MachineryTypeOption>['columns'] = [
    {
      type: 'selection',
      options: ['all', 'none'],
    },
    {
      title: $t('rental.machineryType.name.title'),
      key: 'name',
      width: 240,
    },
    {
      title: $t('rental.machineryType.category.title'),
      key: 'category',
      render: row => $t(`machinery.category.${row.category}`),
    },
  ]

  if (props.isBasePriceSelection) {
    columns.push({
      title: $t('rental.machineryType.hasRentalBasePrice'),
      key: 'rentalBasePrices',
      render: ({ rentalBasePrices }) => rentalBasePrices.length > 0 ? $t('common.yes') : $t('common.no'),
    })
  }

  return columns
})

watch(filters, (newValue, oldValue) => {
  const diffs = Object.values(diff(newValue, oldValue))
  if (diffs.length > 0) {
    selectedMachineryTypes.value = []
  }
})
</script>

<template>
  <TableFilters v-model="search" placeholder="Suchbegriff eingeben (Name)">
    <template v-if="showFilter" #search>
      <n-collapse>
        <n-collapse-item>
          <template #header>
            <span class="text-lg">
              {{ $t('basePrice.machineryType.filter.title') }}
            </span>
          </template>
          <RentalMachineryTypeFilter v-model="filters" />
        </n-collapse-item>
      </n-collapse>
    </template>

    <p>{{ $t('common.selectedCount', { selected: selectedMachineryTypes.length ?? 0, total: machineryTypeOptionsData?.length ?? 0 }) }}</p>
    <TableView
      v-model:checked-row-keys="selectedMachineryTypes"
      :data="machineryTypeOptionsData"
      :columns="machineryTypeOptionsColumns"
      :is-loading="areMachineryTypesOptionsLoading"
      :row-key="row => row.id"
      :initial-pagination-settings="{ pageSize: 20 }"
      class="mt-4"
    />
  </TableFilters>
</template>

<style scoped>
:deep(.n-scrollbar-content) {
  min-width: 100% !important;
}
</style>
